import { audioManager } from './audioManager';

export type SfxNavn =
  | 'cha-ching'
  | 'pageturn'
  | 'bell'
  | 'fanfare'
  | 'click'
  | 'notify';

const SFX_PATHS: Record<SfxNavn, string> = {
  'cha-ching': 'assets/audio/sfx/cha-ching.mp3',
  pageturn: 'assets/audio/sfx/pageturn.mp3',
  bell: 'assets/audio/sfx/bell.mp3',
  fanfare: 'assets/audio/sfx/fanfare.mp3',
  click: 'assets/audio/sfx/click.mp3',
  notify: 'assets/audio/sfx/notify.mp3',
};

const VOLUM_STANDARD = 0.3;
const VOLUM_PER_NAVN: Partial<Record<SfxNavn, number>> = {
  click: 0.18,
  pageturn: 0.22,
  notify: 0.28,
  bell: 0.3,
  'cha-ching': 0.32,
  fanfare: 0.4,
};

const SAMME_LYD_DEMP_MS = 60;

export class SfxManager {
  private cache: Map<SfxNavn, HTMLAudioElement> = new Map();
  private mangler: Set<SfxNavn> = new Set();
  private sisteAvspilling: Map<SfxNavn, number> = new Map();
  private aktiv = true;

  play(navn: SfxNavn): void {
    if (!this.aktiv) return;
    if (audioManager.erAv) return;
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;
    if (this.mangler.has(navn)) return;

    const naa = Date.now();
    const sist = this.sisteAvspilling.get(navn) ?? 0;
    if (naa - sist < SAMME_LYD_DEMP_MS) return;
    this.sisteAvspilling.set(navn, naa);

    try {
      const original = this.last(navn);
      if (!original) return;
      const klone = original.cloneNode(true) as HTMLAudioElement;
      klone.volume = VOLUM_PER_NAVN[navn] ?? VOLUM_STANDARD;
      const result = klone.play();
      if (result && typeof result.catch === 'function') {
        result.catch(() => {
          // browser-policy eller manglende fil — feil stille
        });
      }
    } catch {
      // feil stille
    }
  }

  disable(): void {
    this.aktiv = false;
  }

  enable(): void {
    this.aktiv = true;
  }

  preload(): void {
    if (typeof Audio === 'undefined') return;
    for (const navn of Object.keys(SFX_PATHS) as SfxNavn[]) {
      this.last(navn);
    }
  }

  private last(navn: SfxNavn): HTMLAudioElement | undefined {
    const eksisterende = this.cache.get(navn);
    if (eksisterende) return eksisterende;
    if (this.mangler.has(navn)) return undefined;
    try {
      const audio = new Audio(SFX_PATHS[navn]);
      audio.preload = 'auto';
      audio.volume = VOLUM_PER_NAVN[navn] ?? VOLUM_STANDARD;
      audio.addEventListener('error', () => {
        this.mangler.add(navn);
        this.cache.delete(navn);
      });
      this.cache.set(navn, audio);
      return audio;
    } catch {
      this.mangler.add(navn);
      return undefined;
    }
  }
}

export const sfx = new SfxManager();
